import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { SortDirection } from '@mui/material';
import * as R from 'ramda';

import DeleteModal from '@atom/components/common/DeleteModal';
import {
  CREATE_BUDGET,
  DELETE_BUDGET,
  DUPLICATE_BUDGET,
  GET_BUDGET_TEMPLATES,
  GET_BUDGETS,
  UPDATE_BUDGET,
} from '@atom/graph/budget';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Progress, Snackbar } from '@atom/mui';
import {
  Budget,
  BudgetCreateInput,
  BudgetDuplicateInput,
  BudgetModal,
  BudgetsConnection,
  BudgetsConnectionInput,
  BudgetTemplatesConnection,
  BudgetUpdateInput,
} from '@atom/types/budget';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { deleteBudgetFilters } from '../budgetDetail/useBudgetFilterStorage';

import BudgetsFormModal from './budgetsModal/BudgetsFormModal';
import BudgetsContext, {
  INITIAL_LIMIT,
  INITIAL_PAGE,
  INITIAL_SORT_BY,
  INITIAL_SORT_DIRECTION,
} from './BudgetsContext';
import BudgetsHeader from './BudgetsHeader';
import BudgetsTable from './BudgetsTable';

import classes from './budgets.module.css';

const styles = {
  progress: {
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
};

const BudgetsPortal = () => {
  const [, { navigate }] = useNavigation();
  const [page, setPage] = useState<number>(INITIAL_PAGE);
  const [limit, setLimit] = useState<number>(INITIAL_LIMIT);
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    INITIAL_SORT_DIRECTION,
  );
  const [sortBy, setSortBy] = useState<string>(INITIAL_SORT_BY);
  const [activeModal, setActiveModal] = useState<BudgetModal>(null);
  const [activeFormBudget, setActiveFormBudget] = useState<Budget>(null);
  const [processingModalRequest, setProcessingModalRequest] =
    useState<boolean>(false);

  const handleModalClose = () => {
    setActiveModal(null);
    setActiveFormBudget(null);
    setProcessingModalRequest(false);
  };

  const {
    data,
    loading: loadingBudgets,
    refetch,
  } = useQuery<
    { budgets: BudgetsConnection },
    { input: BudgetsConnectionInput }
  >(GET_BUDGETS, {
    variables: {
      input: {
        page,
        limit,
        sortBy: isNilOrEmpty(sortBy) ? null : `${sortBy},${sortDirection}`,
      },
    },
    fetchPolicy: 'network-only',
  });

  const { data: templatesData, loading: loadingTemplates } = useQuery<{
    budgetTemplates: BudgetTemplatesConnection;
  }>(GET_BUDGET_TEMPLATES);

  const templates = R.pathOr(
    [],
    ['budgetTemplates', 'budgetTemplates'],
    templatesData,
  );

  const [createBudget, { loading: loadingCreate }] = useMutation<
    { budgetCreate: Budget },
    { input: BudgetCreateInput }
  >(CREATE_BUDGET);

  const [updateBudget, { loading: loadingBudget }] = useMutation<
    { budgetUpdate: Budget },
    { input: BudgetUpdateInput }
  >(UPDATE_BUDGET);

  const [duplicateBudget, { loading: loadingDuplicate }] = useMutation<
    { budgetDuplicate: Budget },
    { input: BudgetDuplicateInput }
  >(DUPLICATE_BUDGET);

  const [deleteBudget, { loading: loadingDelete }] = useMutation<
    {},
    { id: string }
  >(DELETE_BUDGET);

  const handleDuplicateBudget = async (input: BudgetDuplicateInput) => {
    try {
      setProcessingModalRequest(true);
      const newBudget = await duplicateBudget({
        variables: {
          input,
        },
      });
      Snackbar.info({
        message: `Created budget "${input.name}"`,
      });
      const newId = R.pathOr(
        null,
        ['data', 'budgetDuplicate', 'id'],
        newBudget,
      );
      if (isNilOrEmpty(newId)) {
        refetch();
      } else {
        navigate(`budget/${newId}`);
      }
    } catch (error) {
      Snackbar.error({
        message:
          'An error occurred while duplicating your budget. Please Try Again.',
      });
    } finally {
      handleModalClose();
    }
  };

  const handleCreateBudget = async (input: BudgetCreateInput) => {
    try {
      setProcessingModalRequest(true);
      const newBudget = await createBudget({
        variables: {
          input,
        },
      });
      Snackbar.info({
        message: `Created budget "${input.name}"`,
      });
      const newId = R.pathOr(null, ['data', 'budgetCreate', 'id'], newBudget);
      if (isNilOrEmpty(newId)) {
        refetch();
      } else {
        navigate(`budget/${newId}`);
      }
    } catch (error) {
      Snackbar.error({
        message:
          'An error occurred while creating your budget. Please Try Again.',
      });
      handleModalClose();
    }
  };

  const handleUpdateBudget = async (input: BudgetUpdateInput) => {
    try {
      setProcessingModalRequest(true);
      await updateBudget({
        variables: {
          input,
        },
      });
      await refetch();
      Snackbar.info({
        message: `Updated budget "${input.name}"`,
      });
      handleModalClose();
    } catch (error) {
      Snackbar.error({
        message:
          'An error occurred while updating your budget. Please Try Again.',
      });
      handleModalClose();
    }
  };

  const handleDeleteBudget = async (budget: Budget) => {
    try {
      setProcessingModalRequest(true);
      await deleteBudget({ variables: { id: budget.id } });
      await refetch();
      deleteBudgetFilters(budget.id);
      handleModalClose();
      Snackbar.info({
        message: `Deleted budget "${budget.name}"`,
      });
    } catch {
      Snackbar.error({
        message: `An error occurred while deleting ${budget.name}. Please Try Again.`,
      });
      handleModalClose();
    }
  };

  const budgets: Budget[] = data?.budgets?.budgets || [];
  const totalCount = data?.budgets?.totalCount || 0;
  const budgetNamesUsed = useMemo(
    () => new Set(budgets.map(budget => budget.name)),
    [budgets],
  );

  const modalOpen = R.includes(activeModal, [
    BudgetModal.CREATE,
    BudgetModal.EDIT,
    BudgetModal.DUPLICATE,
  ]);

  const modalLoading =
    loadingBudget ||
    loadingCreate ||
    loadingDuplicate ||
    loadingDelete ||
    loadingBudgets ||
    loadingTemplates ||
    processingModalRequest;

  return (
    <BudgetsContext.Provider
      value={{
        budgets,
        refetch,
        totalCount,
        page,
        setPage,
        limit,
        setLimit,
        sortDirection,
        setSortDirection,
        sortBy,
        setSortBy,
        setActiveModal,
        setActiveFormBudget,
      }}
    >
      <>
        <BudgetsHeader />
        <div className={classes.container}>
          {loadingBudgets ? (
            <Progress style={styles.progress} />
          ) : (
            <BudgetsTable />
          )}
        </div>
        {modalOpen && (
          <BudgetsFormModal
            onClose={handleModalClose}
            budget={activeFormBudget}
            modalType={activeModal}
            templates={templates}
            budgetNamesUsed={budgetNamesUsed}
            handleCreateBudget={handleCreateBudget}
            handleUpdateBudget={handleUpdateBudget}
            handleDuplicateBudget={handleDuplicateBudget}
            saving={modalLoading}
          />
        )}
        <DeleteModal
          open={activeModal === BudgetModal.DELETE}
          title="Delete Budget?"
          content={
            <>
              Are you sure you want to delete budget "{activeFormBudget?.name}"
              {modalLoading && <Progress size={20} />}
            </>
          }
          onCancel={() => !modalLoading && handleModalClose()}
          onConfirm={() => handleDeleteBudget(activeFormBudget)}
          disabled={modalLoading}
          loading={modalLoading}
        />
      </>
    </BudgetsContext.Provider>
  );
};

export default BudgetsPortal;
